/**
 * Buddy Activity Indicator
 * Compact panel showing buddy status: active count, recent catches, and gold/hour rate.
 * Listens to EventBus for real-time updates.
 */

import { useState, useEffect, useCallback } from 'react';
import { useGameStore } from '@/state';
import { eventBus } from '@/services/EventBus';
import { GameEvent, FishInstance } from '@/types';
import { getFishDefinition, formatCurrency } from '@/data';

interface RecentCatch {
  name: string;
  quality: string;
  timestamp: number;
}

const MAX_RECENT = 3;
const GOLD_TRACKING_WINDOW_MS = 60 * 60 * 1000; // 1 hour window

export function BuddyActivityIndicator() {
  const fishingBuddies = useGameStore((s) => s.playerState.fishingBuddies);
  const haulerBuddies = useGameStore((s) => s.playerState.haulerBuddies);

  const [recentCatches, setRecentCatches] = useState<RecentCatch[]>([]);
  const [goldEvents, setGoldEvents] = useState<{ value: number; timestamp: number }[]>([]);

  const activeFishingBuddies = (fishingBuddies ?? []).filter((b) => b.hired && b.enabled).length;
  const activeHaulers = (haulerBuddies ?? []).filter((b) => b.hired && b.enabled).length;
  const totalActive = activeFishingBuddies + activeHaulers;

  // Listen for buddy fish catches
  const handleBuddyCatch = useCallback((payload: { fish: FishInstance }) => {
    try {
      const def = getFishDefinition(payload.fish.definitionId);
      setRecentCatches((prev) => {
        const next = [
          { name: def.name, quality: payload.fish.quality, timestamp: Date.now() },
          ...prev,
        ];
        return next.slice(0, MAX_RECENT);
      });
    } catch {
      // Fish definition not found — ignore
    }
  }, []);

  // Listen for hauler sell events
  const handleHaulerSold = useCallback((payload: { value: number }) => {
    setGoldEvents((prev) => {
      const now = Date.now();
      const cutoff = now - GOLD_TRACKING_WINDOW_MS;
      const filtered = prev.filter((e) => e.timestamp > cutoff);
      return [...filtered, { value: payload.value, timestamp: now }];
    });
  }, []);

  useEffect(() => {
    const unsub1 = eventBus.on(GameEvent.BUDDY_CAUGHT_FISH, handleBuddyCatch);
    const unsub2 = eventBus.on(GameEvent.HAULER_SOLD_FISH, handleHaulerSold);
    return () => {
      unsub1();
      unsub2();
    };
  }, [handleBuddyCatch, handleHaulerSold]);

  // Calculate gold/hour from recent events
  const goldPerHour = (() => {
    if (goldEvents.length === 0) return 0;
    const now = Date.now();
    const cutoff = now - GOLD_TRACKING_WINDOW_MS;
    const recent = goldEvents.filter((e) => e.timestamp > cutoff);
    const totalGold = recent.reduce((sum, e) => sum + e.value, 0);

    // Scale to per-hour rate based on observation window
    if (recent.length < 2) return totalGold;
    const windowMs = now - recent[0].timestamp;
    if (windowMs <= 0) return totalGold;
    return (totalGold / windowMs) * GOLD_TRACKING_WINDOW_MS;
  })();

  // Don't show if no buddies are hired
  if (totalActive === 0) return null;

  return (
    <div className="w-full max-w-xs mx-auto px-3 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-xs space-y-1.5">
      {/* Header */}
      <div className="flex items-center justify-between text-gray-300">
        <span className="font-medium flex items-center gap-1">
          <span className="text-green-400 text-[10px]">●</span>
          {activeFishingBuddies} rod{activeFishingBuddies !== 1 ? 's' : ''}{activeHaulers > 0 && `, ${activeHaulers} hauler${activeHaulers !== 1 ? 's' : ''}`}
        </span>
        {goldPerHour > 0 && (
          <span className="text-yellow-400">{formatCurrency(Math.round(goldPerHour))}g/hr</span>
        )}
      </div>

      {/* Recent catches */}
      {recentCatches.length > 0 && (
        <div className="space-y-0.5">
          {recentCatches.map((c, i) => (
            <div key={`${c.timestamp}-${i}`} className="flex items-center justify-between text-gray-500">
              <span className="truncate">{c.name}</span>
              <span className={qualityColor(c.quality)}>{c.quality}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function qualityColor(quality: string): string {
  switch (quality) {
    case 'COMMON': return 'text-gray-400';
    case 'UNCOMMON': return 'text-green-400';
    case 'RARE': return 'text-blue-400';
    case 'EPIC': return 'text-purple-400';
    case 'LEGENDARY': return 'text-orange-400';
    case 'UNIQUE': return 'text-yellow-400';
    default: return 'text-gray-400';
  }
}
